/**
 * Pre-distortion of the print texture to compensate for non-uniform stretching
 * during free blow moulding.
 *
 * ── Physical basis ─────────────────────────────────────────────────────────
 * When a flat sheet is inflated into a dome, each triangular mesh element
 * stretches by some factor.  The FEM simulation records this as the "area
 * ratio": areaRatio[t] = current_area[t] / reference_area[t].
 *
 * If a region stretches by areaRatio = A (isotropic assumption), its linear
 * dimensions scale by √A.  A printed feature of size s appears as size s·√A
 * on the inflated dome.  To compensate, the feature in the flat print must be
 * pre-compressed by 1/√A so that after stretching it looks correct.
 *
 * ── Algorithm (Poisson displacement field) ─────────────────────────────────
 * We solve the Poisson equation for a scalar potential φ:
 *
 *   Δφ = 2·(√A − 1)·strength   inside the opening   (source term)
 *   φ  = 0                      on the opening boundary   (Dirichlet BC)
 *
 * The displacement field is:  v = ∇φ = (∂φ/∂x, ∂φ/∂y)
 * For each pixel (px, py):  source = (px + vx, py + vy)
 *
 * Physical interpretation:
 *   Where A > 1 (stretched dome), the source term is positive → φ builds up a
 *   "pressure" inside → ∇φ points outward (toward boundary) → source pixel is
 *   further from centre → centre is pre-compressed. ✓
 *   The zero BC at the boundary forces displacement → 0 there (no wicking). ✓
 *
 * Why Poisson instead of radial / centroid-based formulae?
 *   Radial methods implicitly assume one centre of symmetry, which fails for
 *   non-convex shapes (cat silhouettes, elongated openings, tails) — the global
 *   centroid may lie outside the shape, producing gaps and incorrect displacements.
 *   The Poisson equation is fully shape-aware: the Laplacian operator respects
 *   the actual boundary, distributing the compression smoothly and correctly
 *   even inside narrow protrusions (tails, arms, etc.).
 *
 * ── Area-ratio field smoothing ─────────────────────────────────────────────
 * FEM area ratios are per-triangle (piecewise constant).  Naively
 * rasterising them produces a staircase of visible triangle boundaries in the
 * pre-distorted texture.  We avoid this in two steps:
 *
 *   a) Per-vertex averaging: AR_vertex[v] = area-weighted mean of areaRatios
 *      of all triangles that share vertex v in the current opening.
 *      This converts the discontinuous per-triangle field to a continuous
 *      per-vertex field at every mesh node.
 *
 *   b) Barycentric rasterisation: within each triangle the vertex values are
 *      linearly interpolated → C⁰ continuity across triangle boundaries.
 *
 *   c) Gaussian smoothing (σ ≈ W/80): removes any residual staircase and
 *      high-frequency noise from the triangulation.
 *
 * ── Fallback when no simulation data ───────────────────────────────────────
 * If areaRatios is null or empty, all vertex AR values default to 1.0.
 * scale = 1 → sx = px, sy = py → identity transform (no distortion).
 * The function is therefore safe to call at any time.
 */

import { TriangulationResult } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// 1. Opening mask (rasterisation via canvas 2D)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rasterises the triangles belonging to openingIdx into a binary pixel mask.
 * mask[y*W + x] = 1 means the pixel is inside the opening, 0 means outside.
 * Uses the browser's Canvas 2D fill to handle arbitrary polygon shapes.
 */
function buildOpeningMask(
  tri: TriangulationResult,
  openingIdx: number,
  W: number, H: number,
  widthMm: number, heightMm: number,
): Uint8Array {
  const mmToPixX = (x: number) => (x + widthMm  / 2) / widthMm  * W;
  const mmToPixY = (y: number) => (heightMm / 2 - y) / heightMm * H;

  const mc  = document.createElement('canvas');
  mc.width  = W; mc.height = H;
  const ctx = mc.getContext('2d')!;
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = 'white';

  for (let t = 0; t < tri.triangles.length; t++) {
    if (tri.triLabels[t] !== openingIdx) continue;
    const [a, b, c] = tri.triangles[t];
    ctx.beginPath();
    ctx.moveTo(mmToPixX(tri.vertices[a].x), mmToPixY(tri.vertices[a].y));
    ctx.lineTo(mmToPixX(tri.vertices[b].x), mmToPixY(tri.vertices[b].y));
    ctx.lineTo(mmToPixX(tri.vertices[c].x), mmToPixY(tri.vertices[c].y));
    ctx.closePath();
    ctx.fill();
  }

  const raw  = ctx.getImageData(0, 0, W, H).data;
  const mask = new Uint8Array(W * H);
  for (let i = 0; i < mask.length; i++) mask[i] = raw[i * 4] > 128 ? 1 : 0;
  return mask;
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Per-vertex area ratios (area-weighted average over adjacent triangles)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Converts per-triangle area ratios to per-vertex area ratios for the
 * specified opening.  Each vertex receives the area-weighted mean of the
 * area ratios of all triangles in the opening that share that vertex.
 *
 * Vertices that do not belong to any opening triangle (e.g. boundary nodes
 * that only appear in material triangles) default to 1.0 (no stretch).
 *
 * Area weighting ensures that large triangles contribute proportionally more
 * than small (possibly degenerate) ones, which improves numerical stability.
 */
function buildVertexAreaRatios(
  tri: TriangulationResult,
  openingIdx: number,
  areaRatios: number[],
): Float32Array {
  const n     = tri.vertices.length;
  const sumAR = new Float64Array(n);   // weighted sum of area ratios
  const sumW  = new Float64Array(n);   // sum of weights (reference triangle areas)

  for (let t = 0; t < tri.triangles.length; t++) {
    if (tri.triLabels[t] !== openingIdx) continue;
    const [a, b, c] = tri.triangles[t];

    // Reference area of this triangle (used as weight)
    const vA = tri.vertices[a], vB = tri.vertices[b], vC = tri.vertices[c];
    const w = 0.5 * Math.abs(
      (vB.x - vA.x) * (vC.y - vA.y) - (vC.x - vA.x) * (vB.y - vA.y),
    );

    const ar = areaRatios[t] ?? 1.0;
    for (const vi of [a, b, c]) {
      sumAR[vi] += ar * w;
      sumW[vi]  += w;
    }
  }

  const result = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    result[i] = sumW[i] > 1e-20 ? sumAR[i] / sumW[i] : 1.0;
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Area-ratio raster field (barycentric interpolation of vertex values)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rasterises the per-vertex area ratio values into a W×H pixel grid using
 * barycentric interpolation within each triangle.
 *
 * Pixels not covered by any opening triangle are set to NaN.
 * Pixels inside the opening have a value ≥ 1.0 (the reference area ratio).
 *
 * Because vertex values are used (not constant per-triangle), the result is
 * linearly continuous across triangle boundaries — no visible seams.
 */
function buildAreaRatioField(
  tri: TriangulationResult,
  openingIdx: number,
  vertexAR: Float32Array,
  W: number, H: number,
  widthMm: number, heightMm: number,
): Float32Array {
  const field = new Float32Array(W * H);  // initialised to 0
  const valid = new Uint8Array(W * H);    // 1 = covered by a triangle

  const mmToPixX = (x: number) => (x + widthMm  / 2) / widthMm  * W;
  const mmToPixY = (y: number) => (heightMm / 2 - y) / heightMm * H;

  for (let t = 0; t < tri.triangles.length; t++) {
    if (tri.triLabels[t] !== openingIdx) continue;
    const [ai, bi, ci] = tri.triangles[t];

    const ax = mmToPixX(tri.vertices[ai].x), ay = mmToPixY(tri.vertices[ai].y);
    const bx = mmToPixX(tri.vertices[bi].x), by = mmToPixY(tri.vertices[bi].y);
    const cx = mmToPixX(tri.vertices[ci].x), cy = mmToPixY(tri.vertices[ci].y);
    const aAR = vertexAR[ai], bAR = vertexAR[bi], cAR = vertexAR[ci];

    // Bounding box of this triangle in pixel space
    const minX = Math.max(0,     Math.floor(Math.min(ax, bx, cx)));
    const maxX = Math.min(W - 1, Math.ceil (Math.max(ax, bx, cx)));
    const minY = Math.max(0,     Math.floor(Math.min(ay, by, cy)));
    const maxY = Math.min(H - 1, Math.ceil (Math.max(ay, by, cy)));

    // Precompute barycentric denominator
    const denom = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
    if (Math.abs(denom) < 1e-9) continue;
    const invDenom = 1 / denom;

    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        // Barycentric coordinates of pixel centre w.r.t. this triangle
        const w0 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) * invDenom;
        const w1 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) * invDenom;
        const w2 = 1 - w0 - w1;
        // Accept pixels inside or very slightly outside (guards against gaps)
        if (w0 < -0.01 || w1 < -0.01 || w2 < -0.01) continue;
        const idx = py * W + px;
        field[idx] = w0 * aAR + w1 * bAR + w2 * cAR;
        valid[idx] = 1;
      }
    }
  }

  // Mark uncovered pixels as NaN
  for (let i = 0; i < field.length; i++) {
    if (!valid[i]) field[i] = NaN;
  }
  return field;
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Gaussian smoothing (separable, NaN-aware)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Applies a separable Gaussian blur to a Float32Array field.
 * NaN values (outside the opening) are ignored in the kernel accumulation
 * so that the blur does not bleed across the opening boundary.
 * Output pixels that could not gather any valid neighbours remain NaN.
 */
function gaussianSmooth(
  field: Float32Array, W: number, H: number, sigma: number,
): Float32Array {
  const radius = Math.ceil(2.5 * sigma) | 0;
  const kernel = new Float32Array(2 * radius + 1);
  let ksum = 0;
  for (let k = -radius; k <= radius; k++) {
    kernel[k + radius] = Math.exp(-k * k / (2 * sigma * sigma));
    ksum += kernel[k + radius];
  }
  for (let i = 0; i < kernel.length; i++) kernel[i] /= ksum;

  // Horizontal pass
  const tmp = new Float32Array(W * H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const ci = y * W + x;
      if (isNaN(field[ci])) { tmp[ci] = NaN; continue; }
      let sum = 0, wsum = 0;
      for (let k = -radius; k <= radius; k++) {
        const nx = x + k;
        if (nx < 0 || nx >= W) continue;
        const v = field[y * W + nx];
        if (isNaN(v)) continue;
        const w = kernel[k + radius];
        sum += v * w; wsum += w;
      }
      tmp[ci] = wsum > 0 ? sum / wsum : NaN;
    }
  }

  // Vertical pass
  const out = new Float32Array(W * H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const ci = y * W + x;
      if (isNaN(tmp[ci])) { out[ci] = NaN; continue; }
      let sum = 0, wsum = 0;
      for (let k = -radius; k <= radius; k++) {
        const ny = y + k;
        if (ny < 0 || ny >= H) continue;
        const v = tmp[ny * W + x];
        if (isNaN(v)) continue;
        const w = kernel[k + radius];
        sum += v * w; wsum += w;
      }
      out[ci] = wsum > 0 ? sum / wsum : NaN;
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Z-height field (retained for the height-map export — not used in distortion)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rasterises vertex Z positions (from the FEM simulation) to a pixel grid
 * using barycentric interpolation.  Used only by buildHeightMapDataUrl().
 */
function buildZHeightField(
  tri: TriangulationResult,
  openingIdx: number,
  simZPosMm: number[],
  W: number, H: number,
  widthMm: number, heightMm: number,
): Float32Array {
  const field = new Float32Array(W * H);
  const valid = new Uint8Array(W * H);

  const mmToPixX = (x: number) => (x + widthMm  / 2) / widthMm  * W;
  const mmToPixY = (y: number) => (heightMm / 2 - y) / heightMm * H;

  for (let t = 0; t < tri.triangles.length; t++) {
    if (tri.triLabels[t] !== openingIdx) continue;
    const [ai, bi, ci] = tri.triangles[t];

    const ax = mmToPixX(tri.vertices[ai].x), ay = mmToPixY(tri.vertices[ai].y);
    const bx = mmToPixX(tri.vertices[bi].x), by = mmToPixY(tri.vertices[bi].y);
    const cx = mmToPixX(tri.vertices[ci].x), cy = mmToPixY(tri.vertices[ci].y);
    const az = simZPosMm[ai] ?? 0;
    const bz = simZPosMm[bi] ?? 0;
    const cz = simZPosMm[ci] ?? 0;

    const minX = Math.max(0,     Math.floor(Math.min(ax, bx, cx)));
    const maxX = Math.min(W - 1, Math.ceil (Math.max(ax, bx, cx)));
    const minY = Math.max(0,     Math.floor(Math.min(ay, by, cy)));
    const maxY = Math.min(H - 1, Math.ceil (Math.max(ay, by, cy)));

    const denom = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
    if (Math.abs(denom) < 1e-9) continue;
    const invDenom = 1 / denom;

    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        const w0 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) * invDenom;
        const w1 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) * invDenom;
        const w2 = 1 - w0 - w1;
        if (w0 < -0.01 || w1 < -0.01 || w2 < -0.01) continue;
        const idx = py * W + px;
        field[idx] = w0 * az + w1 * bz + w2 * cz;
        valid[idx] = 1;
      }
    }
  }

  for (let i = 0; i < field.length; i++) {
    if (!valid[i]) field[i] = NaN;
  }
  return field;
}

// ─────────────────────────────────────────────────────────────────────────────
// 6. Gemini inverse-texture-mapping (shape-agnostic primary algorithm)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Applies pre-distortion using inverse texture mapping (the "Gemini" method).
 *
 * For each flat-mesh vertex i we know its flat position and its deformed
 * position after blow-moulding.  The "ideal" source pixel for vertex i is
 * the pixel that corresponds to the deformed XY position (top-down projection):
 *
 *   defPixX[i] = mmToPixX(simXPosMm[i])   (same formula as flat→pixel)
 *   defPixY[i] = mmToPixY(simYPosMm[i])
 *
 * For every pixel (px, py) inside an opening triangle we:
 *   1. Compute barycentric weights (w0, w1, w2) in flat space.
 *   2. Interpolate the deformed pixel position:
 *        sxDef = w0·defX[a] + w1·defX[b] + w2·defX[c]
 *        syDef = w0·defY[a] + w1·defY[b] + w2·defY[c]
 *   3. Blend with identity:
 *        sx = px + (sxDef − px) · strength
 *        sy = py + (syDef − py) · strength
 *   4. Bilinear-sample source at (sx, sy) → write to dst[py, px].
 *
 * Why this works: a flat material point at (px, py) will move to (defPixX, defPixY)
 * after blow-moulding (in top-down projection).  Pre-sampling from that shifted
 * position pre-compresses content in stretched regions so the image appears
 * undistorted after forming.  No centroid or symmetry assumption is needed —
 * arbitrary opening shapes (including non-convex "cat silhouettes") are handled
 * correctly because we rasterise the actual mesh triangles.
 */
function applyGeminiMapping(
  srcData:    Uint8ClampedArray,
  dst:        Uint8ClampedArray,
  W: number,  H: number,
  tri:        TriangulationResult,
  openingIdx: number,
  widthMm:    number,
  heightMm:   number,
  simXPosMm:  number[],
  simYPosMm:  number[],
  strength:   number,
): void {
  const mmToPixX = (x: number) => (x + widthMm  / 2) / widthMm  * W;
  const mmToPixY = (y: number) => (heightMm / 2 - y) / heightMm * H;
  const clamp    = (v: number, max: number) => v < 0 ? 0 : v >= max ? max - 1 : v;

  for (let t = 0; t < tri.triangles.length; t++) {
    if (tri.triLabels[t] !== openingIdx) continue;
    const [ai, bi, ci] = tri.triangles[t];

    // Flat pixel coords of each triangle vertex
    const ax = mmToPixX(tri.vertices[ai].x), ay = mmToPixY(tri.vertices[ai].y);
    const bx = mmToPixX(tri.vertices[bi].x), by = mmToPixY(tri.vertices[bi].y);
    const cx = mmToPixX(tri.vertices[ci].x), cy = mmToPixY(tri.vertices[ci].y);

    // Deformed pixel coords (where the vertex ends up after blow, top-down projection)
    const adx = mmToPixX(simXPosMm[ai]), ady = mmToPixY(simYPosMm[ai]);
    const bdx = mmToPixX(simXPosMm[bi]), bdy = mmToPixY(simYPosMm[bi]);
    const cdx = mmToPixX(simXPosMm[ci]), cdy = mmToPixY(simYPosMm[ci]);

    // Bounding box of the flat triangle
    const minX = Math.max(0,     Math.floor(Math.min(ax, bx, cx)));
    const maxX = Math.min(W - 1, Math.ceil (Math.max(ax, bx, cx)));
    const minY = Math.max(0,     Math.floor(Math.min(ay, by, cy)));
    const maxY = Math.min(H - 1, Math.ceil (Math.max(ay, by, cy)));

    const denom = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);
    if (Math.abs(denom) < 1e-9) continue;
    const inv = 1 / denom;

    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        // Barycentric coords in flat space
        const w0 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) * inv;
        const w1 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) * inv;
        const w2 = 1 - w0 - w1;
        if (w0 < -0.01 || w1 < -0.01 || w2 < -0.01) continue;

        // Interpolated deformed position
        const sxDef = w0 * adx + w1 * bdx + w2 * cdx;
        const syDef = w0 * ady + w1 * bdy + w2 * cdy;

        // strength=0 → identity, strength=1 → full Gemini correction
        const sx = px + (sxDef - px) * strength;
        const sy = py + (syDef - py) * strength;

        // Bilinear sample from source
        const x0 = Math.floor(sx), y0 = Math.floor(sy);
        const x1 = x0 + 1,         y1 = y0 + 1;
        const fx  = sx - x0,        fy = sy - y0;
        const p00 = (clamp(y0, H) * W + clamp(x0, W)) * 4;
        const p10 = (clamp(y0, H) * W + clamp(x1, W)) * 4;
        const p01 = (clamp(y1, H) * W + clamp(x0, W)) * 4;
        const p11 = (clamp(y1, H) * W + clamp(x1, W)) * 4;
        const w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy);
        const w01 = (1 - fx) * fy,        w11 = fx * fy;

        const base = (py * W + px) * 4;
        for (let c = 0; c < 4; c++) {
          dst[base + c] = Math.round(
            srcData[p00 + c] * w00 + srcData[p10 + c] * w10 +
            srcData[p01 + c] * w01 + srcData[p11 + c] * w11,
          );
        }
      }
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. Main export: compute and apply pre-distortion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Computes and applies a pre-distortion to compensate for blow-moulding stretch.
 *
 * Primary algorithm (Gemini inverse texture mapping, when simXPosMm/simYPosMm given):
 *   Uses actual vertex deformation data — see applyGeminiMapping() above.
 *   Works correctly for any opening shape.
 *
 * Fallback algorithm (centroid arc-length ray method, when only areaRatios given):
 *   Casts rays from the mask centroid, builds cumulative arc-length tables
 *   weighted by √areaRatio, and remaps pixels radially.
 *   Works well for circular/convex openings; degrades for non-convex shapes.
 *
 * @param sourceDataUrl  Source texture (data URL)
 * @param tri            Triangulation result
 * @param openingIdx     1-based opening number
 * @param areaRatios     Per-triangle area ratios from FEM; used by fallback
 * @param simZPosMm      Vertex Z positions [mm]; kept for API compatibility
 * @param widthMm        Material width [mm]
 * @param heightMm       Material height [mm]
 * @param strength       Distortion strength 0–2 (1 = full physical correction)
 * @param simXPosMm      Vertex X positions after FEM [mm]; enables Gemini method
 * @param simYPosMm      Vertex Y positions after FEM [mm]; enables Gemini method
 */
export function computeAndApplyDistortion(
  sourceDataUrl: string,
  tri:           TriangulationResult,
  openingIdx:    number,
  areaRatios:    number[] | null,
  simZPosMm:     number[] | null,
  widthMm:       number,
  heightMm:      number,
  strength:      number,
  simXPosMm?:    number[] | null,
  simYPosMm?:    number[] | null,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth;
      const H = img.naturalHeight;

      // ── Canvases ─────────────────────────────────────────────────────────
      const srcCanvas  = document.createElement('canvas');
      srcCanvas.width  = W; srcCanvas.height = H;
      const srcCtx     = srcCanvas.getContext('2d')!;
      srcCtx.drawImage(img, 0, 0);
      const srcData    = srcCtx.getImageData(0, 0, W, H).data;

      const dstCanvas  = document.createElement('canvas');
      dstCanvas.width  = W; dstCanvas.height = H;
      const dstCtx     = dstCanvas.getContext('2d')!;
      // Copy source as base so pixels outside the opening are unchanged
      dstCtx.drawImage(img, 0, 0);
      const dstImg     = dstCtx.getImageData(0, 0, W, H);
      const dst        = dstImg.data;

      // ── Algorithm selection ───────────────────────────────────────────────
      if (simXPosMm && simXPosMm.length >= tri.vertices.length &&
          simYPosMm && simYPosMm.length >= tri.vertices.length) {
        // Primary: Gemini inverse texture mapping
        applyGeminiMapping(
          srcData, dst, W, H, tri, openingIdx, widthMm, heightMm,
          simXPosMm, simYPosMm, strength,
        );
      } else {
        // Fallback: centroid arc-length ray method
        const arArray = (areaRatios && areaRatios.length > 0) ? areaRatios : [];

        // ── Step 1: Opening mask ───────────────────────────────────────────
        const mask = buildOpeningMask(tri, openingIdx, W, H, widthMm, heightMm);

        // ── Step 2: Per-vertex area ratios → smoothed raster field ─────────
        const vertexAR = buildVertexAreaRatios(tri, openingIdx, arArray);
        const arRaw    = buildAreaRatioField(tri, openingIdx, vertexAR, W, H, widthMm, heightMm);
        const sigma    = Math.max(3, Math.min(12, W / 80));
        const arSmooth = gaussianSmooth(arRaw, W, H, sigma);

      // ── Step 4: Directional cumulative arc-length ray tables ─────────────
      //
      // For each angular direction θ from the mask centroid we trace a ray and
      // compute the cumulative arc length s(t, θ) = ∫₀ᵗ √A(ρ, θ) dρ along it.
      // We then normalise so that r_src(t) = R_max(θ) · s(t,θ) / s(R_max(θ),θ),
      // mapping [0 … R_max] → [0 … R_max] monotonically.
      //
      // Where A > 1 (stretched material) s grows faster than t, so r_src > t:
      // the destination pixel at radius t samples from farther out (r_src), which
      // pre-compresses the content toward the centre — exactly the compensation
      // needed for a blow-moulded dome.
      //
      // Key properties vs previous approaches:
      //   • r_src ≤ R_max(θ) by construction → no "blank band" at the boundary
      //   • Mapping is monotone in r → no folding (waving artefacts)
      //   • Per-direction → works for non-convex shapes (cat tail, etc.)
      //   • No PDE solver needed → faster, simpler

      // 4a. Mask centroid
      let cxSum = 0, cySum = 0, cntMask = 0;
      for (let py = 0; py < H; py++)
        for (let px = 0; px < W; px++)
          if (mask[py * W + px]) { cxSum += px; cySum += py; cntMask++; }
      if (cntMask === 0) { resolve(sourceDataUrl); return; }
      const cx = cxSum / cntMask;
      const cy = cySum / cntMask;

      // 4b. Per-angle ray tables
      const N_ANGLES  = 1024;
      const MAX_R     = Math.ceil(Math.hypot(W, H));
      const TWO_PI    = 2 * Math.PI;
      const tableLen  = MAX_R + 2;   // +2 so t0+1 index is always valid
      const rayTable  = new Float32Array(N_ANGLES * tableLen);
      const rMaxTab   = new Float32Array(N_ANGLES);

      for (let k = 0; k < N_ANGLES; k++) {
        const theta = (k + 0.5) / N_ANGLES * TWO_PI;
        const cosT  = Math.cos(theta);
        const sinT  = Math.sin(theta);
        const off   = k * tableLen;

        // Phase 1 — trace ray, build cumulative s(t) stored in rayTable.
        // Tolerance: allow a single missing mask pixel (1-px rasterisation gap
        // between adjacent triangles) before stopping, to avoid a premature
        // rmax that would leave a radial strip of undistorted pixels.
        let rmax = 0;
        rayTable[off] = 0;
        for (let t = 1; t <= MAX_R; t++) {
          const gx = Math.round(cx + t * cosT);
          const gy = Math.round(cy + t * sinT);
          if (gx < 0 || gx >= W || gy < 0 || gy >= H) break;
          if (!mask[gy * W + gx]) {
            // 1-pixel gap tolerance: peek 2 steps ahead
            const gx2 = Math.round(cx + (t + 1) * cosT);
            const gy2 = Math.round(cy + (t + 1) * sinT);
            if (gx2 < 0 || gx2 >= W || gy2 < 0 || gy2 >= H || !mask[gy2 * W + gx2]) break;
          }
          const Av  = arSmooth[gy * W + gx];
          const sqA = (isNaN(Av) || Av < 1) ? 1.0 : Math.sqrt(Av);
          rayTable[off + t] = rayTable[off + t - 1] + sqA;
          rmax = t;
        }
        rMaxTab[k] = rmax;
        const sTotal = (rmax > 0) ? rayTable[off + rmax] : 0;

        // Phase 2 — normalise in-place → r_src values
        for (let t = 0; t < tableLen; t++) {
          rayTable[off + t] = (t <= rmax && sTotal > 1e-9)
            ? rmax * rayTable[off + t] / sTotal
            : rmax;
        }
      }

      // ── Step 5: Pixel loop — directional cumulative arc-length mapping ────
      const clamp = (v: number, max: number) =>
        v < 0 ? 0 : v >= max ? max - 1 : v;

      for (let py = 0; py < H; py++) {
        for (let px = 0; px < W; px++) {
          const i = py * W + px;
          if (!mask[i]) continue;

          const dx = px - cx;
          const dy = py - cy;
          const r  = Math.sqrt(dx * dx + dy * dy);
          if (r < 0.5) continue;   // pixel at centroid → identity

          // Angular index (bilinear between adjacent rays)
          // Rays are centred at (k+0.5)/N·2π, so we subtract 0.5 before flooring
          // to correctly map θ=0 to the midpoint between ray 1023 and ray 0,
          // eliminating the seam discontinuity at the positive-x axis.
          let theta = Math.atan2(dy, dx);
          if (theta < 0) theta += TWO_PI;
          const kfRaw = theta / TWO_PI * N_ANGLES - 0.5;
          const k0    = ((Math.floor(kfRaw) % N_ANGLES) + N_ANGLES) % N_ANGLES;
          const k1    = (k0 + 1) % N_ANGLES;
          const kFrac = kfRaw - Math.floor(kfRaw);

          // Radial index
          const t0   = Math.min(tableLen - 2, Math.floor(r));
          const t1   = t0 + 1;
          const rFrac = r - t0;

          // Bilinear interpolation in (angle × radius) space
          const off0 = k0 * tableLen, off1 = k1 * tableLen;
          const r_src_full =
            rayTable[off0 + t0] * (1 - rFrac) * (1 - kFrac) +
            rayTable[off0 + t1] * rFrac        * (1 - kFrac) +
            rayTable[off1 + t0] * (1 - rFrac)  * kFrac +
            rayTable[off1 + t1] * rFrac         * kFrac;

          // Strength blend: 0 = no change, 1 = full pre-distortion
          const r_src = r + (r_src_full - r) * strength;
          if (r_src < 0.5) continue;

          const scale = r_src / r;
          const sx    = cx + dx * scale;
          const sy    = cy + dy * scale;

          // Bilinear sample from source
          const x0 = Math.floor(sx), y0 = Math.floor(sy);
          const x1 = x0 + 1,         y1 = y0 + 1;
          const fx  = sx - x0,        fy = sy - y0;
          const p00 = (clamp(y0, H) * W + clamp(x0, W)) * 4;
          const p10 = (clamp(y0, H) * W + clamp(x1, W)) * 4;
          const p01 = (clamp(y1, H) * W + clamp(x0, W)) * 4;
          const p11 = (clamp(y1, H) * W + clamp(x1, W)) * 4;
          const w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy);
          const w01 = (1 - fx) * fy,        w11 = fx * fy;

          const base = i * 4;
          for (let c = 0; c < 4; c++) {
            dst[base + c] = Math.round(
              srcData[p00 + c] * w00 + srcData[p10 + c] * w10 +
              srcData[p01 + c] * w01 + srcData[p11 + c] * w11,
            );
          }
        }
      }

      }  // end else (fallback arc-length method)

      dstCtx.putImageData(dstImg, 0, 0);
      resolve(dstCanvas.toDataURL());
    };
    img.onerror = reject;
    img.src = sourceDataUrl;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. Height-map export (for diagnostics — uses Z-field, unchanged)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds a grayscale PNG (data URL) of the smoothed Z-height field for
 * the specified opening.  Black = Z=0, white = Z_max.
 * Used by the "HM" button to visualise the dome shape and diagnose the
 * quality of the height-map data.
 */
export function buildHeightMapDataUrl(
  tri:       TriangulationResult,
  openingIdx: number,
  simZPosMm:  number[] | null,
  widthMm:    number,
  heightMm:   number,
): string {
  const LONG   = 512;
  const aspect = widthMm / heightMm;
  const W      = Math.round(aspect >= 1 ? LONG : LONG * aspect);
  const H      = Math.round(aspect >= 1 ? LONG / aspect : LONG);

  const zArray  = (simZPosMm && simZPosMm.length > 0) ? simZPosMm : [];
  const zRaw    = buildZHeightField(tri, openingIdx, zArray, W, H, widthMm, heightMm);
  const sigma   = Math.max(2, Math.min(8, W / 120));
  const zSmooth = gaussianSmooth(zRaw, W, H, sigma);

  let maxZ = 0;
  for (let i = 0; i < zSmooth.length; i++) {
    if (!isNaN(zSmooth[i]) && zSmooth[i] > maxZ) maxZ = zSmooth[i];
  }

  const canvas  = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx     = canvas.getContext('2d')!;
  const imgData = ctx.createImageData(W, H);
  const px      = imgData.data;

  for (let i = 0; i < W * H; i++) {
    const z    = zSmooth[i];
    const gray = isNaN(z) ? 0 : (maxZ > 0 ? Math.round(z / maxZ * 255) : 0);
    px[i * 4]     = gray;
    px[i * 4 + 1] = gray;
    px[i * 4 + 2] = gray;
    px[i * 4 + 3] = 255;
  }

  ctx.putImageData(imgData, 0, 0);
  return canvas.toDataURL('image/png');
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. Opening boundary edges (for yellow contour in Viewport3D)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the boundary edges of openingIdx — edges shared by exactly one
 * opening triangle and one non-opening (material) triangle.
 * Used to draw the yellow highlight contour in the 3D viewport.
 */
export function getOpeningBoundaryEdges(
  tri:        TriangulationResult,
  openingIdx: number,
): [number, number][] {
  const edgeLabels = new Map<string, Set<number>>();
  for (let t = 0; t < tri.triangles.length; t++) {
    const [a, b, c] = tri.triangles[t];
    const tag = tri.triLabels[t] === openingIdx ? 1 : 0;
    for (const [u, v] of [[a, b], [b, c], [c, a]] as [number, number][]) {
      const key = u < v ? `${u}_${v}` : `${v}_${u}`;
      if (!edgeLabels.has(key)) edgeLabels.set(key, new Set());
      edgeLabels.get(key)!.add(tag);
    }
  }
  const result: [number, number][] = [];
  for (const [key, tags] of Array.from(edgeLabels)) {
    if (tags.has(0) && tags.has(1)) {
      const [u, v] = key.split('_').map(Number);
      result.push([u, v]);
    }
  }
  return result;
}
